/**
 * Expenses API Service
 * 
 * Backend API endpoints:
 * - POST /api/expenses/upload-statement          (PDF upload + parsing)
 * - GET  /api/expenses/:year/:month              (monthly query)
 * - POST /api/expenses/receipt                   (batch receipt upload)
 * - DELETE /api/expenses/:expenseId/receipt       (receipt delete)
 * - GET  /api/expenses/statement/:id/download    (original PDF download)
 * - GET  /api/expenses/:year/:month/receipts-zip (all receipts ZIP)
 */

import api from './api';

/**
 * Upload relevé de compte PDF(s) and parse transactions
 * @param {File[]} files - PDF files
 * @param {number} year 
 * @param {number} month 
 * @returns {Promise<{success, statements, expenses}>}
 */
export const uploadStatement = async (files, year, month) => {
    const formData = new FormData();
    formData.append('year', year);
    formData.append('month', month);
    files.forEach(f => formData.append('files', f));
    return api.upload('/expenses/upload-statement', formData);
};

/**
 * Get expenses for a specific month
 * @param {number} year 
 * @param {number} month 
 * @returns {Promise<{success, statements, expenses, summary}>}
 */
export const getExpenses = async (year, month) => {
    return api.get(`/expenses/${year}/${month}`);
};

/**
 * Per-month flags for the month picker (rows to verify, periods to reissue).
 * @param {number} year
 * @returns {Promise<{success, year, months: Array<{month, total, verified, unverified, periodStatus, needsReview, needsReissue}>}>}
 */
export const getMonthStatus = async (year) => {
    return api.get(`/expenses/${year}/month-status`);
};

/**
 * Get invoice-ready matched credit data. Effective draft values are already
 * resolved by the backend while original values remain available for audit.
 */
export const getExpensesExportData = async (year, month) => {
    return api.get(`/expenses/${year}/${month}/export-data`);
};

/**
 * Upload a receipt for one or more expenses
 * @param {File} file - Receipt file (image or PDF)
 * @param {number[]} expenseIds - Array of expense IDs
 * @returns {Promise<{success, updated_ids, receipt_url}>}
 */
export const uploadReceipt = async (file, expenseIds) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('expense_ids', expenseIds.join(','));
    return api.upload('/expenses/receipt', formData);
};

/**
 * Delete a receipt reference from an expense
 * @param {number} expenseId 
 * @returns {Promise<{success}>}
 */
export const deleteReceipt = async (expenseId) => {
    return api.delete(`/expenses/${expenseId}/receipt`);
};

/**
 * Link an existing receipt to multiple expenses (share/group)
 * @param {number} sourceExpenseId - Expense that has the receipt
 * @param {number[]} targetExpenseIds - Expenses to link the receipt to
 * @returns {Promise<{success, updated_ids, receipt_url}>}
 */
export const linkReceipt = async (sourceExpenseId, targetExpenseIds) => {
    return api.post('/expenses/link-receipt', {
        source_expense_id: sourceExpenseId,
        target_expense_ids: targetExpenseIds
    });
};

/**
 * Mark expenses as CPA-handled (no receipt needed)
 * @param {number[]} expenseIds
 * @returns {Promise<{success, updated_ids}>}
 */
export const markCPA = async (expenseIds) => {
    return api.post('/expenses/mark-cpa', { expense_ids: expenseIds });
};

/**
 * Unmark expenses from CPA status
 * @param {number[]} expenseIds
 * @returns {Promise<{success, updated_ids}>}
 */
export const unmarkCPA = async (expenseIds) => {
    return api.post('/expenses/unmark-cpa', { expense_ids: expenseIds });
};

/**
 * Update note/comment on an expense
 * @param {number} expenseId
 * @param {string} note
 * @returns {Promise<{success}>}
 */
export const updateNote = async (expenseId, note) => {
    return api.post(`/expenses/${expenseId}/note`, { note });
};

/**
 * Update the final review status of an expense
 * @param {number} expenseId
 * @param {boolean} checked
 * @returns {Promise<{success, expense}>}
 */
export const setFinalCheck = async (expenseId, checked) => {
    return api.patch(`/expenses/${expenseId}/final-check`, { checked });
};

const normalizeExpenseIds = expenses => {
    const values = Array.isArray(expenses) ? expenses : [expenses];
    return [...new Set(values.map(value => Number(value?.id ?? value)).filter(Number.isInteger))];
};

export const getPaymentCandidates = async expenses => {
    const expenseIds = normalizeExpenseIds(expenses);
    if (expenseIds.length === 0) throw new Error('Aucun crédit sélectionné');
    const query = expenseIds.length > 1 ? `?expense_ids=${expenseIds.join(',')}` : '';
    return api.get(`/expenses/${expenseIds[0]}/payment-candidates${query}`);
};

export const savePaymentReconciliation = async (expenses, matches, draftUpdates) => {
    const expenseIds = normalizeExpenseIds(expenses);
    if (expenseIds.length === 0) throw new Error('Aucun crédit sélectionné');
    return api.put(`/expenses/${expenseIds[0]}/payment-reconciliation`, {
        expense_ids: expenseIds,
        matches,
        draft_updates: draftUpdates
    });
};

/**
 * Add a payment seen on the bank statement but missing from reservations and
 * commissions. The bank credit scopes the request (owner, period lock).
 */
export const createManualPayment = async (expenseId, payment) => {
    return api.post(`/expenses/${expenseId}/manual-payments`, payment);
};

export const deleteManualPayment = async manualPaymentId => {
    return api.delete(`/expenses/manual-payments/${manualPaymentId}`);
};

export const dissolvePaymentGroup = async expenseId => {
    return api.delete(`/expenses/${expenseId}/payment-group`);
};

/**
 * Delete a statement and all its parsed expenses
 * @param {number} statementId 
 * @returns {Promise<{success}>}
 */
export const deleteStatement = async (statementId) => {
    return api.delete(`/expenses/statement/${statementId}`);
};

/**
 * Download the original statement PDF
 * @param {number} statementId 
 */
export const downloadStatement = (statementId) => {
    return api.download(`/expenses/statement/${statementId}/download`, `Releve_${statementId}.pdf`);
};

/**
 * Download all receipts as ZIP for a month
 * @param {number} year 
 * @param {number} month 
 */
export const downloadReceiptsZip = (year, month) => {
    return api.download(
        `/expenses/${year}/${month}/receipts-zip`,
        `Justificatifs_${year}_${String(month).padStart(2, '0')}.zip`
    );
};

const expensesApi = {
    uploadStatement,
    getExpenses,
    getMonthStatus,
    getExpensesExportData,
    uploadReceipt,
    deleteReceipt,
    linkReceipt,
    markCPA,
    unmarkCPA,
    updateNote,
    setFinalCheck,
    getPaymentCandidates,
    savePaymentReconciliation,
    createManualPayment,
    deleteManualPayment,
    dissolvePaymentGroup,
    deleteStatement,
    downloadStatement,
    downloadReceiptsZip,
};

export default expensesApi;
